import { Formik } from 'formik';
import { useCallback, useContext } from 'react';

import { vendorsApi } from 'src/app/services/Vendors/Vendors.service';
import {
  type IVendorProductLeadContactTabFormValues,
  toVendorLeadContactTabInitialValues,
  vendorProductLeadContactTabValidationSchema,
} from 'src/components/vendors/steps/Contact/VendorLeadStepContactTab.validation';
import { VendorLeadStepContactTabContent } from 'src/components/vendors/steps/Contact/VendorLeadStepContactTabContent';
import { getContactsDiff } from 'src/helpers/getContactsDiff';
import { toApiContact } from 'src/helpers/toApiContact';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';

type VendorLeadStepContactTabProps = {
  vendorProductLeadId: string;
  onSubmit: () => boolean;
};

export function VendorLeadStepContactTab({ vendorProductLeadId, onSubmit }: Readonly<VendorLeadStepContactTabProps>) {
  const { vendorLeadProduct, updateVendorLeadProduct } = useContext(VendorsContext);

  const [, partialUpdateVendorLeadProduct] = vendorsApi.usePartialUpdateVendorProductLeadMutation(vendorProductLeadId);
  const [, manualGetVendorProductLead] = vendorsApi.useManualGetVendorProductLeadQuery(vendorProductLeadId);

  const [, createVendorLeadContact] = vendorsApi.useCreateVendorLeadContactMutation(vendorProductLeadId);
  const updateVendorLeadContact = vendorsApi.useUpdateVendorLeadContactMutation(vendorProductLeadId);
  const deleteVendorLeadContact = vendorsApi.useDeleteVendorLeadContactMutation(vendorProductLeadId);

  const save = useCallback(
    async (newValues: IVendorProductLeadContactTabFormValues, submit: boolean) => {
      const { addedContacts, updatedContacts, removedContacts } = getContactsDiff(
        newValues.contacts,
        vendorLeadProduct?.contacts ?? []
      );

      await Promise.all([
        ...addedContacts.map((addedContact) =>
          createVendorLeadContact({
            data: {
              ...toApiContact(addedContact),
            },
          })
        ),
        ...updatedContacts.map((updatedContact) => updateVendorLeadContact(toApiContact(updatedContact))),
        ...removedContacts.map((removedContactId) => deleteVendorLeadContact(removedContactId)),
      ]);

      const [updatedVendorLeadProductData] = await Promise.all([
        // Do additional request to get final version of vendor lead
        manualGetVendorProductLead(),
        ...(submit ? [partialUpdateVendorLeadProduct({ data: { contactList: { submit: true } } })] : []),
      ]);

      updateVendorLeadProduct(updatedVendorLeadProductData.data);
    },
    [
      vendorLeadProduct,
      createVendorLeadContact,
      updateVendorLeadContact,
      deleteVendorLeadContact,
      partialUpdateVendorLeadProduct,
      manualGetVendorProductLead,
      updateVendorLeadProduct,
    ]
  );

  const handleSubmit = useCallback(
    async (submitValues: IVendorProductLeadContactTabFormValues) => {
      await save(submitValues, true);

      return onSubmit();
    },
    [save, onSubmit]
  );

  const handleUpdate = useCallback(
    (newValues: IVendorProductLeadContactTabFormValues) => save(newValues, false),
    [save]
  );

  if (!vendorLeadProduct) {
    return null;
  }

  return (
    <Formik<IVendorProductLeadContactTabFormValues>
      initialValues={toVendorLeadContactTabInitialValues(vendorLeadProduct)}
      validationSchema={vendorProductLeadContactTabValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => <VendorLeadStepContactTabContent formikProps={formikProps} onUpdate={handleUpdate} />}
    </Formik>
  );
}
